/**
 * Blog Article Page
 * Data-driven blog post display with SEO optimization
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import BookAppointmentLink, { BOOK_APPOINTMENT_ARIA_LABEL } from '../components/BookAppointmentLink';
import SEO from '../components/SEO';
import Breadcrumbs, { createBreadcrumbs } from '../components/Breadcrumbs';
import { RelatedPosts, RelatedLocations } from '../components/InternalLinks';
import Footer from '../components/Footer';
import { getBlogPostBySlug } from '../data/seo';
import { getFullBlogPost, BlogSection } from '../data/blogContent';
import { 
  generateArticleSchema, 
  generateBlogPostMeta,
} from '../utils/seo';

// ============================================================================
// CONTENT RENDERER
// ============================================================================

interface ContentRendererProps {
  sections: BlogSection[];
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ sections }) => {
  return (
    <div className="prose prose-lg max-w-none">
      <style>{`
        .prose h2 {
          color: #1a365d;
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose h3 {
          color: #2d3748;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .prose p {
          color: #4a5568;
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }
        .prose .lead {
          font-size: 1.25rem;
          color: #2d3748;
          font-weight: 500;
          border-left: 4px solid #319795;
          padding-left: 1rem;
          margin-bottom: 2rem;
        }
        .prose ul {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .prose li {
          color: #4a5568;
          margin-bottom: 0.5rem;
          position: relative;
        }
        .prose li::marker {
          color: #319795;
        }
        .prose blockquote {
          border-left: 4px solid #319795;
          padding-left: 1rem;
          margin: 2rem 0;
          font-style: italic;
          color: #2d3748;
          background: #f0fdfa;
          padding: 1rem 1.5rem;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        .prose .cta-box {
          background: linear-gradient(135deg, #f0fdfa 0%, #e6fffa 100%);
          border: 2px solid #319795;
          border-radius: 1rem;
          padding: 2rem;
          margin: 2rem 0;
          text-align: center;
        }
        .prose .cta-box h3 {
          color: #1a365d;
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          margin-top: 0;
        }
        .prose .cta-box p {
          margin-bottom: 1rem;
        }
        .prose .btn-primary {
          display: inline-block;
          background: #319795;
          color: white;
          padding: 0.75rem 2rem;
          border-radius: 0.5rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s;
        }
        .prose .btn-primary:hover {
          background: #2c7a7b;
        }
      `}</style>
      
      {sections.map((section, index) => {
        switch (section.type) {
          case 'paragraph':
            return (
              <p key={index} className={index === 0 ? 'lead' : ''}>
                {section.content}
              </p>
            );
          
          case 'sources':
            return <div key={index} className="mt-8 border-t border-gray-200 pt-6">
              <h2>Sources and further reading</h2>
              <ul>{section.links?.map(link => <li key={link.url}><a href={link.url} className="text-brand-teal underline">{link.label}</a></li>)}</ul>
            </div>;

          case 'heading':
            if (section.level === 3) {
              return <h3 key={index}>{section.content}</h3>;
            }
            return <h2 key={index}>{section.content}</h2>;
          
          case 'list':
            return (
              <ul key={index}>
                {section.items?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            );
          
          case 'quote':
            return (
              <blockquote key={index}>
                {section.content}
              </blockquote>
            );
          
          case 'cta': {
            const ctaTo = section.ctaLink || '/book';
            return (
              <div key={index} className="cta-box">
                <h3>{section.content}</h3>
                <p>Request a consultation with our veterinary team.</p>
                <Link
                  to={ctaTo}
                  className="btn-primary"
                  {...(ctaTo === '/book' ? { 'aria-label': BOOK_APPOINTMENT_ARIA_LABEL } : {})}
                >
                  {section.ctaText || 'Book Now'}
                </Link>
              </div>
            );
          }
          
          case 'image':
            return (
              <figure key={index} className="my-8">
                <img 
                  src={section.imageUrl} 
                  alt={section.imageAlt || ''} 
                  className="w-full rounded-xl shadow-lg"
                  loading="lazy"
                />
                {section.imageAlt && (
                  <figcaption className="text-center text-sm text-gray-500 mt-2">
                    {section.imageAlt}
                  </figcaption>
                )}
              </figure>
            );
          
          default:
            return null;
        }
      })}
    </div>
  );
};

// ============================================================================
// BLOG ARTICLE PAGE
// ============================================================================

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Get post data
  const postMeta = slug ? getBlogPostBySlug(slug) : undefined;
  const fullPost = slug ? getFullBlogPost(slug) : undefined;
  
  // 404 handling
  if (!postMeta || !slug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-brand-teal/10 rounded-full flex items-center justify-center">
            <i className="fas fa-file-alt text-brand-teal text-4xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8 max-w-md">
            The article you're looking for doesn't exist or may have been moved.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/blog" className="btn-primary px-6 py-3">
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Blog
            </Link>
            <Link to="/" className="btn-secondary px-6 py-3">
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  const meta = generateBlogPostMeta(postMeta);
  const breadcrumbs = createBreadcrumbs.blogPost(postMeta.title, slug);
  
  return (
    <>
      <SEO 
        meta={meta}
        schema={generateArticleSchema(postMeta)}
        breadcrumbs={breadcrumbs}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <section className="bg-gradient-to-br from-brand-teal/10 to-white py-12">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={breadcrumbs} className="mb-6" />
              
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full text-sm font-semibold">
                  {postMeta.category}
                </span>
                <span className="text-gray-500 text-sm">{postMeta.date}</span>
                <span className="text-gray-500 text-sm">• {postMeta.readTime}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight font-nantes mb-6">
                {postMeta.title}
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                {postMeta.excerpt}
              </p>
              
              {/* Author info */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-teal rounded-full flex items-center justify-center">
                  <i className="fas fa-tooth text-white"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{postMeta.author}</p>
                  <p className="text-sm text-gray-500">Veterinary Dental Care</p>
                </div>
              </div>
              
              {postMeta.image && (
                <div className="mt-8 rounded-2xl overflow-hidden shadow-xl h-[300px] md:h-[400px]">
                  <img 
                    src={postMeta.image} 
                    alt={postMeta.imageAlt || postMeta.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12">
          <div className="container-custom">
            <div className="grid lg:grid-cols-4 gap-12">
              {/* Main Content */}
              <article className="lg:col-span-3 max-w-3xl">
                {fullPost ? (
                  <ContentRenderer sections={fullPost.sections} />
                ) : (
                  <div className="prose prose-lg">
                    <p className="lead">{postMeta.excerpt}</p>
                    <div className="cta-box">
                      <h3>Want to Learn More?</h3>
                      <p>Contact our team for personalized advice about your pet's dental health.</p>
                      <BookAppointmentLink className="btn-primary">Book Consultation</BookAppointmentLink>
                    </div>
                  </div>
                )}
                
                {/* Tags */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {postMeta.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Share */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">Share This Article</h4>
                  <div className="flex gap-3">
                    <a 
                      href={`https://www.facebook.com/sharer/sharer.php?u=${meta.canonical}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                      aria-label="Share on Facebook (opens in new tab)"
                    >
                      <i className="fab fa-facebook-f" aria-hidden="true"></i>
                    </a>
                    <a 
                      href={`https://twitter.com/intent/tweet?url=${meta.canonical}&text=${encodeURIComponent(postMeta.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                      aria-label="Share on Twitter (opens in new tab)"
                    >
                      <i className="fab fa-twitter" aria-hidden="true"></i>
                    </a>
                    <a 
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${meta.canonical}&title=${encodeURIComponent(postMeta.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-blue-800 text-white rounded-full flex items-center justify-center hover:bg-blue-900 transition-colors"
                      aria-label="Share on LinkedIn (opens in new tab)"
                    >
                      <i className="fab fa-linkedin-in" aria-hidden="true"></i>
                    </a>
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-8">
                {/* Related Posts */}
                <RelatedPosts 
                  currentPost={postMeta} 
                  limit={3}
                  title="Related Articles"
                />
                
                {/* Locations */}
                <RelatedLocations 
                  title="Find Us Near You" 
                  limit={4}
                />
                
                {/* Quick CTA */}
                <div className="bg-brand-teal text-white rounded-xl p-6 sticky top-24">
                  <h3 className="text-lg font-bold mb-3">Need Dental Care?</h3>
                  <p className="opacity-90 text-sm mb-4">
                    Schedule your pet's dental cleaning today.
                  </p>
                  <BookAppointmentLink
                    className="block w-full bg-white text-brand-teal py-3 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors"
                  >
                    Book Now
                  </BookAppointmentLink>
                  <a
                    href="tel:+19544663775"
                    className="block w-full text-center text-white mt-3 hover:underline"
                  >
                    <i className="fas fa-phone mr-2"></i>
                    (954) 466-3775
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related CTA */}
        <section className="bg-brand-teal text-white py-12">
          <div className="container-custom text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Improve Your Pet's Dental Health?
            </h2>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Schedule a free consultation with our veterinary dental specialists in Fort Lauderdale.
            </p>
            <BookAppointmentLink
              className="inline-block bg-white text-brand-teal px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all"
            >
              Book Free Consultation
            </BookAppointmentLink>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticle;
